/**
 * Displays a single extracted health insurance policy.
 *
 * Default view = top 5 fields that matter most at glance:
 *   1. Insurer + plan
 *   2. Sum insured (+ cover type: floater/individual/topup)
 *   3. Premium (+ frequency)
 *   4. Renewal date
 *   5. Members covered count
 *
 * "Show more" reveals waiting periods (critical for claim eligibility!),
 * insured members list, cost caps, benefits, TPA + claim contacts.
 */
import * as React from 'react';
import { Badge, Button, Card, CardContent, CardHeader, CardTitle } from '@finmin/ui';
import {
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  HeartPulse,
  Info,
  Mail,
  Phone,
  Users,
} from 'lucide-react';
import type { HealthInsurancePolicyExtracted } from '@/api/profile-documents';
import {
  formatCoverType,
  formatDate,
  formatFrequency,
  formatINR,
  formatINRCompact,
} from './format';

interface Props {
  policy: HealthInsurancePolicyExtracted;
  /** When true, details are open on first render. Default false. */
  defaultExpanded?: boolean;
}

export function HealthPolicyCard({ policy: p, defaultExpanded = false }: Props) {
  const [expanded, setExpanded] = React.useState(defaultExpanded);

  const headlineTitle = p.insurer_name ?? 'Health insurance policy';
  const subtitle = [p.plan_name, p.policy_number && `#${p.policy_number}`]
    .filter(Boolean)
    .join(' · ');

  const benefits: Array<[string, boolean | null]> = [
    ['Cashless', p.is_cashless_available],
    ['Maternity', p.maternity_benefit],
    ['OPD', p.opd_benefit],
    ['Critical illness rider', p.critical_illness_addon],
    ['Personal accident rider', p.personal_accident_addon],
    ['Hospital cash', p.hospital_cash_addon],
    ['AYUSH', p.ayush_coverage],
    ['Day-care procedures', p.day_care_covered],
  ];
  const activeBenefits = benefits.filter(([, v]) => v === true).map(([k]) => k);

  const hasWaiting = [
    p.pre_existing_disease_waiting_months,
    p.specific_disease_waiting_months,
    p.maternity_waiting_months,
    p.initial_waiting_period_days,
  ].some((v) => v != null);

  const hasLimits =
    !!p.room_rent_limit ||
    p.copay_pct != null ||
    p.deductible_inr != null ||
    p.cumulative_bonus_pct != null;

  const hasDetails =
    hasWaiting ||
    hasLimits ||
    p.insured_members.length > 0 ||
    activeBenefits.length > 0 ||
    p.riders.length > 0 ||
    !!p.claim_helpline ||
    !!p.tpa_phone ||
    !!p.claims_email ||
    !!p.extraction_notes;

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <CardTitle className="text-base flex items-center gap-2">
              <HeartPulse className="h-4 w-4 text-muted-foreground shrink-0" aria-hidden />
              <span className="truncate">{headlineTitle}</span>
            </CardTitle>
            {subtitle && (
              <p className="text-xs text-muted-foreground mt-1 truncate">{subtitle}</p>
            )}
          </div>
          {p.is_manually_edited && (
            <Badge variant="outline" className="shrink-0 text-xs">
              Edited
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* ── Top 5 (always visible) ─────────────────────────────────────── */}
        <div className="grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-4">
          <Stat
            label="Sum insured"
            value={formatINRCompact(p.sum_insured)}
            sub={p.cover_type ? formatCoverType(p.cover_type) : undefined}
          />
          <Stat
            label="Premium"
            value={formatINRCompact(p.premium_amount)}
            sub={formatFrequency(p.premium_frequency)}
          />
          <Stat label="Renews" value={formatDate(p.renewal_date ?? p.policy_period_end)} />
          <Stat
            label="Members"
            value={p.insured_members.length > 0 ? String(p.insured_members.length) : '—'}
          />
        </div>

        {/* ── Details (collapsed by default) ─────────────────────────────── */}
        {expanded && hasDetails && (
          <div className="space-y-5 border-t border-border pt-5">
            {hasWaiting && (
              <div className="rounded-md border border-warning/30 bg-warning/5 p-3">
                <div className="flex items-center gap-1.5 mb-2">
                  <AlertTriangle className="h-3.5 w-3.5 text-warning" aria-hidden />
                  <Label>Waiting periods</Label>
                </div>
                <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm sm:grid-cols-4">
                  <Mini
                    label="Initial"
                    value={
                      p.initial_waiting_period_days
                        ? `${p.initial_waiting_period_days} d`
                        : '—'
                    }
                  />
                  <Mini
                    label="Pre-existing"
                    value={
                      p.pre_existing_disease_waiting_months
                        ? `${p.pre_existing_disease_waiting_months} mo`
                        : '—'
                    }
                  />
                  <Mini
                    label="Specific dis."
                    value={
                      p.specific_disease_waiting_months
                        ? `${p.specific_disease_waiting_months} mo`
                        : '—'
                    }
                  />
                  <Mini
                    label="Maternity"
                    value={
                      p.maternity_waiting_months
                        ? `${p.maternity_waiting_months} mo`
                        : '—'
                    }
                  />
                </dl>
              </div>
            )}

            {hasLimits && (
              <div>
                <Label>Limits & bonus</Label>
                <dl className="mt-1.5 grid grid-cols-2 gap-x-4 gap-y-2 text-sm sm:grid-cols-4">
                  {p.room_rent_limit && (
                    <Mini label="Room rent" value={p.room_rent_limit} />
                  )}
                  {p.icu_limit && <Mini label="ICU" value={p.icu_limit} />}
                  {p.copay_pct != null && (
                    <Mini label="Co-pay" value={`${p.copay_pct}%`} />
                  )}
                  {p.deductible_inr != null && (
                    <Mini
                      label="Deductible"
                      value={formatINRCompact(p.deductible_inr)}
                    />
                  )}
                  {p.cumulative_bonus_pct != null && (
                    <Mini label="NCB earned" value={`${p.cumulative_bonus_pct}%`} />
                  )}
                </dl>
              </div>
            )}

            {p.insured_members.length > 0 && (
              <div>
                <Label>
                  <Users className="h-3 w-3 inline mr-1" aria-hidden />
                  Insured members
                </Label>
                <ul className="mt-1.5 space-y-1">
                  {p.insured_members.map((m, i) => (
                    <li
                      key={`${m.name ?? 'member'}-${i}`}
                      className="text-sm text-foreground"
                    >
                      {m.name ?? '—'}
                      {m.relationship && (
                        <span className="text-muted-foreground"> · {m.relationship}</span>
                      )}
                      {m.age != null && (
                        <span className="text-muted-foreground"> · {m.age} yrs</span>
                      )}
                      {m.is_proposer && (
                        <Badge variant="outline" className="ml-2 text-xs">
                          proposer
                        </Badge>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {(activeBenefits.length > 0 || p.riders.length > 0) && (
              <div>
                <Label>Benefits & riders</Label>
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  {activeBenefits.map((b) => (
                    <Badge key={b} variant="secondary">
                      {b}
                    </Badge>
                  ))}
                  {p.riders.map((r) => (
                    <Badge key={r} variant="secondary">
                      {r}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {(p.claim_helpline || p.tpa_phone || p.claims_email) && (
              <div className="rounded-md bg-secondary/40 p-3 space-y-1.5">
                <Label>For hospitalisation / claims</Label>
                {p.claim_helpline && (
                  <ContactLine
                    icon={<Phone className="h-3.5 w-3.5 text-muted-foreground" />}
                    href={`tel:${p.claim_helpline}`}
                    value={p.claim_helpline}
                    meta="24x7 claim helpline"
                  />
                )}
                {p.tpa_phone && (
                  <ContactLine
                    icon={<Phone className="h-3.5 w-3.5 text-muted-foreground" />}
                    href={`tel:${p.tpa_phone}`}
                    value={p.tpa_phone}
                    meta={p.tpa_name ? `TPA · ${p.tpa_name}` : 'TPA'}
                  />
                )}
                {p.claims_email && (
                  <ContactLine
                    icon={<Mail className="h-3.5 w-3.5 text-muted-foreground" />}
                    href={`mailto:${p.claims_email}`}
                    value={p.claims_email}
                  />
                )}
              </div>
            )}

            {p.extraction_notes && (
              <div className="flex items-start gap-2 text-xs text-muted-foreground">
                <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" aria-hidden />
                <p>{p.extraction_notes}</p>
              </div>
            )}

            <p className="text-xs text-muted-foreground">
              Sum insured:{' '}
              <span className="font-medium">{formatINR(p.sum_insured)}</span>
              {p.network_hospital_count != null && (
                <>
                  {' '}
                  · {p.network_hospital_count.toLocaleString('en-IN')} network hospitals
                </>
              )}{' '}
              · Extracted {formatDate(p.extracted_at)}
            </p>
          </div>
        )}

        {/* ── Toggle ─────────────────────────────────────────────────────── */}
        {hasDetails && (
          <div className="flex justify-center">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setExpanded((v) => !v)}
              className="gap-1.5 text-xs"
            >
              {expanded ? (
                <>
                  Show less
                  <ChevronUp className="h-3 w-3" aria-hidden />
                </>
              ) : (
                <>
                  Show more
                  <ChevronDown className="h-3 w-3" aria-hidden />
                </>
              )}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function Stat({
  label,
  value,
  sub,
}: {
  label: string;
  value: string;
  sub?: string;
}) {
  return (
    <div>
      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
        {label}
      </p>
      <p className="text-sm font-semibold text-foreground mt-0.5 tabular-nums">
        {value}
      </p>
      {sub && <p className="text-xs text-muted-foreground mt-0.5 truncate">{sub}</p>}
    </div>
  );
}

function Mini({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="text-sm font-medium text-foreground tabular-nums">{value}</dd>
    </div>
  );
}

function Label({ children }: { children: React.ReactNode }) {
  return (
    <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
      {children}
    </span>
  );
}

function ContactLine({
  icon,
  href,
  value,
  meta,
}: {
  icon: React.ReactNode;
  href: string;
  value: string;
  meta?: string;
}) {
  return (
    <div className="flex items-center gap-2 text-sm">
      {icon}
      <a href={href} className="hover:underline">
        {value}
      </a>
      {meta && <span className="text-xs text-muted-foreground">{meta}</span>}
    </div>
  );
}
